import { useState } from 'react'
import { Modal, PlusIcon } from './modal.jsx'
import {
  InvalidCloudAccountError,
  isRole,
  roleHasCapability,
  validateRegistration,
} from '@qim/shared'
import { ApiError, registerAccount } from '../api.js'
import type { RegisteredAccountResult } from '../api.js'
import type { FormEvent, JSX } from 'react'

/**
 * Registering a cloud account, and handing over the external ID it mints.
 *
 * ## Why this screen matters more than it looks
 *
 * This is the one write in the product that cannot be repeated. The API mints
 * the external ID here, returns it exactly once, and there is deliberately no
 * read path afterwards — the listing projects columns that exclude the
 * ciphertext, and recovering one is unbuilt on purpose because it should be its
 * own audited action.
 *
 * So this component has exactly one chance to put that value in front of a
 * person, and it has to *say* that rather than looking like something they can
 * come back for. It was previously a `curl`, where the value scrolled past in a
 * terminal — the worst available place for it.
 *
 * ## Why the form calls the API's own validator
 *
 * `validateRegistration` is the function the handler uses. Calling it here means
 * the form refuses exactly what the API refuses, with the same message, before
 * making a request — rather than a second set of rules that can drift. It lives
 * in `shared` precisely so both sides can reach it.
 */
export const RegisterAccount = ({
  baseUrl,
  tenantId,
  role,
  onRegistered,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly onRegistered: () => void
}): JSX.Element | null => {
  const [awsAccountId, setAwsAccountId] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [result, setResult] = useState<RegisteredAccountResult | undefined>(undefined)
  const [open, setOpen] = useState(false)

  if (!isRole(role) || !roleHasCapability(role, 'account:onboard')) return null

  /** The API's own rules, run here so a refusal costs no round trip. */
  const problem = ((): string | undefined => {
    if (awsAccountId === '' && label === '') return undefined
    try {
      validateRegistration({ cloud: 'aws', awsAccountId, label })
      return undefined
    } catch (error: unknown) {
      return error instanceof InvalidCloudAccountError
        ? error.message
        : 'Those details are not valid.'
    }
  })()

  const ready = awsAccountId !== '' && label.trim() !== '' && problem === undefined

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setFailure(undefined)
    try {
      const registered = await registerAccount(baseUrl, tenantId, {
        cloud: 'aws',
        awsAccountId,
        label: label.trim(),
      })
      setOpen(false)
      setResult(registered)
      setAwsAccountId('')
      setLabel('')
      /*
       * Deliberately not `onRegistered()` here.
       *
       * That reloads the customer screen, and until IM-88 the reload blanked it
       * to `loading` — unmounting this component and destroying `result` before
       * the panel below could render. The external ID is minted once and has no
       * read path, so the reload silently threw away the only copy of the value
       * the customer needs, leaving an account nobody can finish onboarding.
       *
       * `App` no longer unmounts on a re-read, which is the actual fix. This is
       * the second half: the list refreshes when the panel is dismissed, so
       * there is never a reload in flight while the irreplaceable value is on
       * screen. A few seconds of a stale account list is not a comparable cost.
       */
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 409
          ? // Specifically, not generically: an account already registered is a
            // different situation from a malformed request, and the person is
            // probably looking at the account they already have.
            `That AWS account is already registered for this customer. It is in the list above.`
          : error instanceof ApiError && error.status === 400
            ? 'The control plane refused those details.'
            : 'Could not register the account just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (result !== undefined) {
    return (
      <section className="panel">
        <h3>Registered {result.account.label}</h3>
        {/* role="alert" deliberately: this is the only time this value is ever
          shown, so it should be announced rather than merely rendered. */}
        <p role="alert">
          <strong>Copy the external ID now.</strong> It is shown once and cannot be retrieved again
          — not by this console, not by the API, not from the database. If it is lost, the account
          has to be registered again.
        </p>
        <dl className="detail">
          <dt>External ID</dt>
          <dd>
            <code>{result.externalId}</code>
          </dd>
          <dt>Qrvey principal</dt>
          {/* What the customer's trust policy will name. Read-only by design —
            never the deploy role. */}
          <dd>
            <code>{result.diagnosticsPrincipal}</code>
          </dd>
          <dt>Account</dt>
          <dd>{result.account.accountKey}</dd>
        </dl>
        <p className="muted">
          Both values go into the access template, which is on this account&rsquo;s onboarding
          screen with the external ID already filled in — so the usual path is to go there rather
          than to copy this by hand.
        </p>
        {/* Dismissal is what refreshes the list — see `submit`. */}
        <button
          type="button"
          onClick={() => {
            setResult(undefined)
            onRegistered()
          }}
        >
          Done
        </button>
      </section>
    )
  }

  if (!open) {
    return (
      <div className="panel-action">
        <button type="button" className="primary" onClick={() => setOpen(true)}>
          <PlusIcon />
          Register account
        </button>
      </div>
    )
  }

  return (
    /*
     * The *form* is a modal; the result deliberately is not (IM-146).
     *
     * What comes back is the external ID, which is shown exactly once and
     * cannot be retrieved afterwards by anything. A modal can be dismissed by
     * Esc or a click on the backdrop, and losing that value to a stray click
     * means registering the account again — so the result stays a panel with an
     * explicit Done.
     */
    <Modal title="Register a cloud account" onCancel={() => setOpen(false)}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="field">
          <label htmlFor="awsAccountId">AWS account id</label>
          <input
            id="awsAccountId"
            value={awsAccountId}
            onChange={(event) => setAwsAccountId(event.target.value)}
            inputMode="numeric"
            aria-describedby="awsAccountId-hint"
          />
          <span id="awsAccountId-hint" className="hint">
            Exactly 12 digits.
          </span>
        </div>
        <div className="field">
          <label htmlFor="accountLabel">Label</label>
          <input
            id="accountLabel"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            aria-describedby="accountLabel-hint"
          />
          <span id="accountLabel-hint" className="hint">
            What this account is for — &ldquo;Trial&rdquo;, &ldquo;Production&rdquo;.
          </span>
        </div>
        {problem === undefined ? null : <p className="failure">{problem}</p>}
        <p role="note" className="muted">
          Registering mints an external ID that is shown <strong>once</strong>, on the next screen.
          Have somewhere to put it before you continue.
        </p>
        <div className="actions">
          <button type="button" className="ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy || !ready}>
            {busy ? 'Registering…' : 'Register account'}
          </button>
        </div>
        {failure === undefined ? null : <p className="failure">{failure}</p>}
      </form>
    </Modal>
  )
}
